import React from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "../Datas/productData";
import "../css/privacy.scss";

const privacyNavigation = [
  ["privacy-scope", "Scope and controller"],
  ["privacy-information", "Information we handle"],
  ["privacy-use", "How information is used"],
  ["privacy-payment", "Payment and providers"],
  ["privacy-sharing", "Sharing and transfers"],
  ["privacy-retention", "Retention and security"],
  ["privacy-rights", "Your choices and rights"],
  ["privacy-contact", "Contact"],
];

function Privacy() {
  const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <main className="privacy">
      <div className="privacy__shell">
        <nav className="privacy__breadcrumb" aria-label="Breadcrumb">
          <Link to="/" onClick={toTop}>Home</Link><span aria-hidden="true">/</span><span aria-current="page">Privacy policy</span>
        </nav>

        <header className="privacy__header">
          <p className="privacy__kicker">Legal information</p>
          <h1>Privacy policy</h1>
          <p className="privacy__updated">Last reviewed 23 October 2026</p>
          <p className="privacy__lede">
            This policy explains how {siteConfig.brandName} handles information when you browse products,
            use the cart, contact us or continue to our hosted payment service.
          </p>
        </header>

        <aside className="privacy__demo" aria-label="Privacy summary">
          <span aria-hidden="true">i</span>
          <p><strong>Privacy at a glance.</strong> We collect only the information needed to provide the requested service, and payment credentials are entered directly with the payment provider rather than on this website.</p>
        </aside>

        <div className="privacy__layout">
          <nav className="privacy__contents" aria-label="Privacy policy contents">
            <p>On this page</p>
            <ol>{privacyNavigation.map(([id, label]) => <li key={id}><a href={`#${id}`}>{label}</a></li>)}</ol>
          </nav>

          <article className="privacy__content">
            <section id="privacy-scope" className="privacy__section">
              <p className="privacy__number">01</p><h2>Scope and controller</h2>
              <p>This notice covers the {siteConfig.brandName} website, catalogue, cart, checkout information form and customer-service pages. The store operates from {siteConfig.contact.address} in {siteConfig.operatingCountry}. Questions about our privacy practices may be sent to <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a>.</p>
              <p>Third-party payment pages and other linked services publish their own privacy notices. We recommend reading those notices before providing information directly to those services.</p>
            </section>

            <section id="privacy-information" className="privacy__section">
              <p className="privacy__number">02</p><h2>Information we handle</h2>
              <p>Depending on how you use the website, information may include:</p>
              <ul>
                <li>your name, email address, delivery address and optional order note;</li>
                <li>products, quantities and options placed in your cart or wishlist;</li>
                <li>order reference, amount, currency and payment outcome;</li>
                <li>messages you choose to send to customer service; and</li>
                <li>technical records such as IP address, browser type, device information, access time and security events created by hosting infrastructure.</li>
              </ul>
              <p>Cart and wishlist selections are stored locally in your browser so they remain available after a refresh. You can remove this data through the cart controls or your browser settings.</p>
            </section>

            <section id="privacy-use" className="privacy__section">
              <p className="privacy__number">03</p><h2>How information is used</h2>
              <p>We use relevant information to prepare and deliver orders, provide order updates, respond to requests, maintain website security, prevent misuse, keep required business records and improve the reliability of our services. We do not use the checkout form to enrol visitors in marketing.</p>
              <p>Under the GDPR, processing may rely on performance of a contract, compliance with a legal obligation, a legitimate operational interest that does not override individual rights, or consent for an optional feature. Consent can be withdrawn for future processing at any time.</p>
            </section>

            <section id="privacy-payment" className="privacy__section">
              <p className="privacy__number">04</p><h2>Payment and checkout providers</h2>
              <p>When you select Pay now, the website sends the order reference, total, currency, contact email and first product title to the hosted payment service. Payment card or wallet credentials are entered on that provider&apos;s secure page and are not collected in the {siteConfig.brandName} checkout form.</p>
              <p>The provider returns a success or cancellation status to this website. Fraud-prevention and authentication checks may also be performed by the provider, card network or issuing bank under their own terms.</p>
            </section>

            <section id="privacy-sharing" className="privacy__section">
              <p className="privacy__number">05</p><h2>Sharing and international transfers</h2>
              <p>Information may be shared only as needed with hosting, payment, fraud-prevention, fulfilment, delivery and customer-support providers, as well as professional advisers or authorities where the law requires it. We do not sell customer contact lists.</p>
              <p>Providers may process information outside Germany or the European Economic Area. Where the GDPR requires it, transfers use an adequacy decision or other appropriate legal safeguards, and the information shared is limited to what the service needs.</p>
            </section>

            <section id="privacy-retention" className="privacy__section">
              <p className="privacy__number">06</p><h2>Retention and security</h2>
              <p>Order and support records are retained only for the time needed to provide service, resolve disputes, prevent fraud and meet accounting or legal requirements. Browser storage remains on your device until you clear it. Records no longer needed are deleted or anonymised where practical.</p>
              <p>We use safeguards appropriate to the information, including encrypted transport, access controls and service monitoring. No internet service can guarantee absolute security, so please contact us promptly if you believe information has been exposed.</p>
            </section>

            <section id="privacy-rights" className="privacy__section">
              <p className="privacy__number">07</p><h2>Your choices and rights</h2>
              <p>Under the GDPR, you may request access to or correction of personal information, ask for deletion or restricted use, object to certain processing, or request a portable copy where the relevant conditions apply. You may also complain to the Bayerisches Landesamt für Datenschutzaufsicht (BayLDA), the data-protection supervisory authority for private-sector organisations in Bavaria, or another competent supervisory authority.</p>
              <p>For information about browser technologies and controls, read our <Link to="/cookies" onClick={toTop}>cookie policy</Link>. This policy may be updated when services or legal requirements change; the review date above identifies the current version.</p>
            </section>

            <section id="privacy-contact" className="privacy__section privacy__section--contact">
              <p className="privacy__number">08</p><h2>Contact</h2>
              <p>Email <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a> during {siteConfig.contact.hours.toLowerCase()}. Please describe the request clearly and include the relevant order number where applicable. Postal correspondence may be sent to {siteConfig.contact.address}.</p>
              <Link className="privacy__button" to="/contact" onClick={toTop}>Contact us <span aria-hidden="true">→</span></Link>
            </section>
          </article>
        </div>
      </div>
    </main>
  );
}

export default Privacy;
